/**
 * Reviews Marquee — Auto-scroll + drag-to-scrub + wheel scroll
 */
class ReviewsMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.reviews-track');
    if (!this.track) return;

    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.wheelTimeout = null;

    this.addEventListener('mousedown', this.onPointerDown.bind(this));
    this.addEventListener('touchstart', this.onPointerDown.bind(this), { passive: true });
    window.addEventListener('mousemove', this.onPointerMove.bind(this));
    window.addEventListener('touchmove', this.onPointerMove.bind(this), { passive: false });
    window.addEventListener('mouseup', this.onPointerUp.bind(this));
    window.addEventListener('touchend', this.onPointerUp.bind(this));
    this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
  }

  getX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  getCurrentOffset() {
    const transform = getComputedStyle(this.track).transform;
    if (!transform || transform === 'none') return 0;
    const matrix = new DOMMatrix(transform);
    return matrix.m41;
  }

  onPointerDown(e) {
    this.isDragging = true;
    this.startX = this.getX(e);
    this.pauseAtCurrent();
    this.classList.add('is-dragging');
  }

  onPointerMove(e) {
    if (!this.isDragging) return;
    if (e.cancelable) e.preventDefault();

    const x = this.getX(e);
    const delta = x - this.startX;
    let newTranslate = this.currentTranslate + delta;

    // Wrap within bounds (track is duplicated, so half-width is the loop point)
    const halfWidth = this.track.scrollWidth / 2;
    if (newTranslate > 0) newTranslate -= halfWidth;
    if (newTranslate < -halfWidth) newTranslate += halfWidth;

    this.track.style.transform = `translateX(${newTranslate}px)`;
  }

  onPointerUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.classList.remove('is-dragging');
    this.resumeFromCurrent();
  }

  onWheel(e) {
    // Use deltaX for horizontal scroll, fall back to deltaY for vertical scroll wheels
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (delta === 0) return;
    e.preventDefault();

    // Pause animation and grab current position if not already paused
    if (!this.wheelTimeout) {
      this.pauseAtCurrent();
    } else {
      this.currentTranslate = this.getCurrentOffset();
      clearTimeout(this.wheelTimeout);
    }

    let newTranslate = this.currentTranslate - delta;

    const halfWidth = this.track.scrollWidth / 2;
    if (newTranslate > 0) newTranslate -= halfWidth;
    if (newTranslate < -halfWidth) newTranslate += halfWidth;

    this.currentTranslate = newTranslate;
    this.track.style.transform = `translateX(${newTranslate}px)`;

    // Resume auto-scroll after 1.5s of no wheel activity
    this.wheelTimeout = setTimeout(() => {
      this.wheelTimeout = null;
      this.resumeFromCurrent();
    }, 1500);
  }

  pauseAtCurrent() {
    this.currentTranslate = this.getCurrentOffset();
    this.track.style.animation = 'none';
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  resumeFromCurrent() {
    const offset = this.getCurrentOffset();
    const halfWidth = this.track.scrollWidth / 2;
    const progress = Math.abs(offset) / halfWidth;
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-speed')) || 250;

    this.track.style.transform = '';
    this.track.style.animation = 'none';
    this.track.offsetHeight; // force reflow
    this.track.style.animation = `marquee ${speed}s linear infinite`;
    this.track.style.animationDelay = `-${speed * progress}s`;
  }
}

if (!customElements.get('reviews-marquee')) {
  customElements.define('reviews-marquee', ReviewsMarquee);
}
